import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, of, shareReplay, throwError } from 'rxjs';
import { utils } from '../helper/utils';
import { GeoIndex, GeoTree } from '../models/geography-tree.model';
import { buildIndex } from '../shared-components/aems-geo-filter/geo-index';

const ENDPOINT = 'geography-tree';
const CACHE_KEY = 'aems.geography.tree';

interface CacheEntry {
  etag: string;
  tree: GeoTree;
}

/**
 * Loads the accessible geography tree once per session and hands out the built index.
 *
 * The index is held here rather than in the component so opening a second geography filter anywhere
 * in the app is instant and rebuilds nothing. Responses carry a weak ETag derived from the snapshot
 * version *and* the caller's own shop set, so it changes both when master data moves and when this
 * user's access changes — which makes it safe to persist the payload across reloads.
 */
@Injectable({ providedIn: 'root' })
export class GeographyService {
  private readonly httpClient = inject(HttpClient);

  private index$: Observable<GeoIndex> | null = null;

  /** The built index, fetched on first use and replayed to every later caller. */
  getIndex(): Observable<GeoIndex> {
    this.index$ ??= this.fetchTree().pipe(
      map(buildIndex),
      catchError((error: unknown) => {
        // Don't let a failed load poison the cache — the next mount (or Retry) tries again.
        this.index$ = null;
        return throwError(() => error);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.index$;
  }

  /** Drops the in-memory and persisted copies and reloads from the API. */
  refresh(): Observable<GeoIndex> {
    this.index$ = null;
    this.clearCache();
    return this.getIndex();
  }

  private fetchTree(): Observable<GeoTree> {
    const cached = this.readCache();
    const headers = cached ? new HttpHeaders({ 'If-None-Match': cached.etag }) : undefined;

    return this.httpClient.get<GeoTree>(utils.GetMasterDataUrl(ENDPOINT), { observe: 'response', headers }).pipe(
      map((response) => {
        const tree = response.body;
        if (!tree) {
          // A 304 handled transparently by the browser cache arrives here with an empty body.
          if (cached) return cached.tree;
          throw new Error('geography-tree responded without a body');
        }

        this.writeCache({ etag: response.headers.get('ETag') ?? '', tree });
        return tree;
      }),
      catchError((error: unknown) => {
        // HttpClient surfaces a 304 as an error because it is outside the 2xx range.
        if (error instanceof HttpErrorResponse && error.status === 304 && cached) {
          return of(cached.tree);
        }
        return throwError(() => error);
      }),
    );
  }

  private readCache(): CacheEntry | null {
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (!raw) return null;

      const entry = JSON.parse(raw) as CacheEntry;
      return entry?.etag && entry?.tree ? entry : null;
    } catch {
      return null;
    }
  }

  private writeCache(entry: CacheEntry): void {
    if (!entry.etag) return;

    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(entry));
    } catch {
      // Quota or a privacy mode that refuses storage — the tree still works, it just refetches.
    }
  }

  private clearCache(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch {
      // Nothing to do — a cache we cannot clear is a cache we cannot have written either.
    }
  }
}
